import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Settings, Check, Plus, Trash2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DecimalInput } from "@/components/ui/DecimalInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { allowedUnitsForProduct, classifyUnit } from "@/lib/treatmentDosing";
import { formatStock } from "@/lib/formatStock";
import { convertQuantity } from "@/lib/unitConversion";

export interface TreatmentProduct {
  product_id: string;
  product_name: string;
  /** Resolved absolute quantity to apply / deduct. Always derived from the rate. */
  amount: number;
  /** Unit of the resolved absolute amount (L, kg, mL, g, units, pkg). */
  unit: string;
  stock: number;
  is_from_treatment: boolean;
  /** The editable rate the user typed. */
  rate_value: number;
  /** The rate unit: absolute (L/kg/...), per-area (L/ha...) or per-caldo (mL/100L, g/100L). */
  rate_unit: string;
  /** The product's inventory unit, used to filter compatible units and convert. */
  stock_unit: string;
  dose_value?: number | null;
  dose_unit?: string | null;
  amount_per_ha?: number | null;
  units_per_package?: number | null;
  original_unit?: string;
  /** Aviso bajo la dosis (tarjeta de voz: «tu dosis habitual»). El formulario manual no lo usa. */
  rate_hint?: string | null;
}

interface InventoryProduct {
  id: string;
  product_name: string;
  unit: string;
  quantity: number;
}

export interface GreenhouseAreaInfo {
  greenhouse_name: string;
  area_ha: number;
}

interface TreatmentProductsSectionProps {
  products: TreatmentProduct[];
  inventoryProducts: InventoryProduct[];
  isCustomizing: boolean;
  onCustomizingChange: (customizing: boolean) => void;
  onProductRateValueChange: (index: number, value: number) => void;
  onProductRateUnitChange: (index: number, unit: string) => void;
  onRemoveProduct: (index: number) => void;
  onAddProduct: (productId: string) => void;
  numGreenhouses: number;
  totalBlocks: number;
  showAlwaysVisible?: boolean;
  totalAreaHa?: number;
  /** Total resolved caldo (spray mix) in liters, for showing the calculation. */
  caldoL?: number | null;
  /** True when at least one product is dosed per caldo but no caldo volume is set yet. */
  caldoMissing?: boolean;
  greenhouseAreaBreakdown?: GreenhouseAreaInfo[];
  /** Optional water volume controls rendered inside the same card. */
  waterVolume?: number | null;
  waterVolumeUnit?: string;
  onWaterVolumeChange?: (value: number | null) => void;
  onWaterVolumeUnitChange?: (unit: string) => void;
  /**
   * Una línea debajo del nombre de cada producto (F4, 26-09-2026): el aviso de
   * su nº de registro. Solo informa; no cambia nada de la fila ni de lo que se
   * guarda.
   */
  renderProductNote?: (productId: string) => ReactNode;
  /**
   * «Control de stock: Flexible» de la empresa (`useStockControl`): que falte no
   * para el tratamiento, así que la fila no va en rojo sino en ámbar con
   * «quedará en −X · faltan albaranes». Sin él, lo de siempre.
   */
  stockFlexible?: boolean;
}

export default function TreatmentProductsSection({
  products,
  inventoryProducts,
  isCustomizing,
  onCustomizingChange,
  onProductRateValueChange,
  onProductRateUnitChange,
  onRemoveProduct,
  onAddProduct,
  numGreenhouses,
  totalBlocks,
  showAlwaysVisible = false,
  totalAreaHa = 0,
  caldoL = null,
  caldoMissing = false,
  greenhouseAreaBreakdown,
  waterVolume,
  waterVolumeUnit,
  onWaterVolumeChange,
  onWaterVolumeUnitChange,
  renderProductNote,
  stockFlexible = false,
}: TreatmentProductsSectionProps) {
  const { t } = useTranslation();

  const availableProducts = inventoryProducts.filter(
    ip => !products.some(p => p.product_id === ip.id)
  );

  if (products.length === 0 && !showAlwaysVisible) {
    return null;
  }

  const hasAreaBreakdown = greenhouseAreaBreakdown && greenhouseAreaBreakdown.length > 1;
  const totalBreakdownArea = hasAreaBreakdown
    ? greenhouseAreaBreakdown.reduce((sum, g) => sum + g.area_ha, 0)
    : 0;

  const fmtNum = (n: number) =>
    (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const showWaterRow = onWaterVolumeChange !== undefined;

  // Con un producto dosificado por agua (cc/100L…) el volumen deja de ser opcional: sin
  // él la cantidad sale 0.00 y la tarea no se puede calcular. La etiqueta lo dice.
  const hasPerCaldoProduct = products.some((p) => classifyUnit(p.rate_unit) === "per_caldo");
  const waterLabel = t(hasPerCaldoProduct ? "sprayLogs.waterVolumeRequired" : "sprayLogs.waterVolumeOptional");

  return (
    <div className="space-y-4">
    <div className="rounded-2xl border border-[#b6dded] bg-white p-3.5 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-bold text-base text-foreground truncate">
          {t("planner.estimatedProductUse")}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onCustomizingChange(!isCustomizing)}
          aria-label={isCustomizing ? t("sprayLogs.done") : t("sprayLogs.customize")}
          className="h-8 w-8 rounded-full border border-[#b6dded] bg-white shrink-0 hover:bg-muted/40"
        >
          {isCustomizing ? <Check className="h-4 w-4" /> : <Settings className="h-4 w-4" />}
        </Button>
      </div>


      {/* Summary sub-row: area · sections · caldo */}
      {(totalAreaHa > 0 || totalBlocks > 0 || (caldoL && caldoL > 0)) && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {totalAreaHa > 0 && (
            <span className="font-semibold text-foreground">
              {totalAreaHa.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              ha
            </span>
          )}
          {totalBlocks > 0 && (
            <span className="text-muted-foreground">
              · {totalBlocks}{" "}
              {totalBlocks === 1
                ? t("planner.summary.seccion")
                : t("planner.summary.secciones")}
            </span>
          )}
          {/* Cuando falta el agua ya no se avisa aquí: lo dice —en rojo— la etiqueta del
              propio campo de abajo, y salían los dos avisos idénticos uno sobre otro. */}
          {caldoL && caldoL > 0 ? (
            <span className="text-muted-foreground">
              · {fmtNum(caldoL)} L {waterLabel.toLowerCase()}
            </span>
          ) : null}
        </div>
      )}

      {/* Water volume row (folded into the same card) */}
      {showWaterRow && (
        <div className="mb-3">
          <div className={`text-xs font-medium mb-1 ${caldoMissing ? "text-destructive" : "text-muted-foreground"}`}>
            {waterLabel}
          </div>
          <div className="flex items-center gap-2">
            <DecimalInput
              value={waterVolume ?? null}
              onValueChange={(num) => onWaterVolumeChange?.(num)}
              placeholder={t("sprayLogs.notIndicated")}
              className={`h-10 flex-1 min-w-0 text-sm bg-white shadow-sm rounded-md placeholder:text-xs placeholder:text-muted-foreground/70 ${caldoMissing ? "border-destructive" : "border border-[#b6dded]"}`}
            />
            <Select value={waterVolumeUnit || "L"} onValueChange={(v) => onWaterVolumeUnitChange?.(v)}>
              <SelectTrigger className="h-10 w-24 shrink-0 text-sm bg-white shadow-sm border border-[#b6dded] rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="L">L</SelectItem>
                <SelectItem value="L/ha">L/ha</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}





      {products.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          {showAlwaysVisible ? (
            <p>{t("planner.addProductsManually")}</p>
          ) : (
            <p>{t("sprayLogs.noProductsConfigured")}</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {products.map((product, index) => {
            const basis = classifyUnit(product.rate_unit);
            const isRate = basis !== "absolute";
            const needsCaldo = basis === "per_caldo" && caldoMissing;
            const allowed = allowedUnitsForProduct(product.stock_unit || product.unit);
            const unitGroups: Array<{ label: string; units: string[] }> = [
              { label: t("planner.units"), units: allowed.absolute },
              { label: "ha / m²", units: allowed.perArea },
              { label: "caldo (/100L)", units: allowed.perCaldo },
            ].filter(g => g.units.length > 0);
            // Always keep the product's current unit selectable (legacy units, casing, etc.)
            const allOptionUnits = unitGroups.flatMap(g => g.units);
            if (product.rate_unit && !allOptionUnits.includes(product.rate_unit)) {
              unitGroups.unshift({ label: "actual", units: [product.rate_unit] });
            }

            /* Hasta 4 decimales: con 2, una dosis de 0,025 se leía «0.03» y 3,4 mL «0». */
            const fmt = (n: number) => parseFloat((n || 0).toFixed(4));
            // Human-readable calculation: how the absolute amount was derived.
            let formula = "";
            if (basis === "per_area") {
              formula = `${fmt(product.rate_value)} ${product.rate_unit} × ${totalAreaHa.toFixed(2)} ha`;
            } else if (basis === "per_caldo") {
              formula = caldoL && caldoL > 0
                ? `${fmt(product.rate_value)} ${product.rate_unit} × ${fmt(caldoL)} L`
                : "";
            }

            /* El aviso de «no hay bastante» compara PERAS CON PERAS: la cantidad se
               pasa antes a la unidad del inventario. Comparando a pelo, 500 g de un
               producto que se lleva en kg salían en rojo contra 1 kg de stock. Si las
               unidades no son convertibles entre sí, no se puede afirmar que falte:
               no se pinta en rojo. */
            const amountInStockUnit = convertQuantity(
              product.amount,
              product.unit,
              product.stock_unit || product.unit,
            );
            const faltaStock = amountInStockUnit != null && amountInStockUnit > product.stock;
            /* «Control de stock: Flexible»: que falte no para el tratamiento; se
               descuenta igual y el producto queda en negativo. No es un error que
               corregir sino un aviso: «quedará en −X · faltan albaranes». */
            const shortOfStock = faltaStock && !stockFlexible;
            const quedaEn = faltaStock && stockFlexible ? product.stock - (amountInStockUnit as number) : null;

            return (
              <div
                key={product.product_id}
                className={`p-2 rounded ${
                  needsCaldo || shortOfStock
                    ? 'bg-destructive/10 border border-destructive/30'
                    : quedaEn != null
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-background'
                }`}
              >
                {isCustomizing ? (
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-semibold break-words">{product.product_name}</span>
                    {renderProductNote?.(product.product_id)}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Texto con teclado decimal: con `type="number"` «0,05»
                          llegaba como 5 (auditoría del perito C2; decimalInput.ts). */}
                      <DecimalInput
                        value={product.rate_value}
                        onValueChange={(num) => onProductRateValueChange(index, num ?? 0)}
                        aria-label={t("treatments.amount")}
                        className="w-20 h-8 text-sm"
                      />
                      <Select value={product.rate_unit} onValueChange={(v) => onProductRateUnitChange(index, v)}>
                        <SelectTrigger className="w-28 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {unitGroups.map(group => (
                            group.units.map(u => (
                              <SelectItem key={u} value={u}>{u}</SelectItem>
                            ))
                          ))}
                          {product.units_per_package && product.units_per_package > 0 && !allowed.absolute.includes("pkg") && (
                            <SelectItem value="pkg">{t("planner.packages")}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveProduct(index)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive ml-auto"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Derived absolute amount + how it was calculated */}
                    {needsCaldo ? (
                      <div className="text-xs text-destructive font-medium">
                        ↓ {t("sprayLogs.waterVolumeRequired")}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-base font-bold text-primary">
                            {fmt(product.amount)} {product.unit}
                          </span>
                          {product.rate_unit === "pkg" && product.units_per_package && product.units_per_package > 0 && product.amount > 0 && (
                            <span className="text-primary text-xs">
                              ({fmt(product.amount * product.units_per_package)} {t("planner.units")})
                            </span>
                          )}
                          <span className="text-muted-foreground text-xs">
                            Stock: {fmt(product.stock)} {product.stock_unit || product.unit}
                            {quedaEn != null && (
                              <span data-testid="queda-en-negativo" className="text-[#C77F1E] font-medium">
                                {" · "}{t("planner.quedaEnNegativo", "quedará en {{qty}} {{unit}} · faltan albaranes", { qty: fmt(quedaEn), unit: product.stock_unit || product.unit })}
                              </span>
                            )}
                          </span>
                        </div>
                        {formula && (
                          <div className="text-xs text-muted-foreground bg-muted/60 rounded px-2 py-1 w-fit">
                            {formula} = <span className="font-semibold text-foreground">{fmt(product.amount)} {product.unit}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-semibold break-words">{product.product_name}</span>
                    {renderProductNote?.(product.product_id)}
                    <div className="flex flex-col gap-1.5 text-sm">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-base font-bold text-[#2E84DE]">{fmt(product.amount)} {product.unit}</span>
                        <span className="text-muted-foreground text-xs">
                          Stock: {fmt(product.stock)} {product.stock_unit || product.unit}
                            {quedaEn != null && (
                              <span data-testid="queda-en-negativo" className="text-[#C77F1E] font-medium">
                                {" · "}{t("planner.quedaEnNegativo", "quedará en {{qty}} {{unit}} · faltan albaranes", { qty: fmt(quedaEn), unit: product.stock_unit || product.unit })}
                              </span>
                            )}
                        </span>
                      </div>
                      {/* How the amount was calculated (only when the input was a rate) */}
                      {isRate && formula && (
                        <div className="text-xs text-muted-foreground bg-muted/60 rounded px-2 py-1 w-fit break-words">
                          {formula} = <span className="font-semibold text-foreground">{fmt(product.amount)} {product.unit}</span>
                        </div>
                      )}
                      {/* Per-greenhouse breakdown (proportional by area) */}
                      {hasAreaBreakdown && totalBreakdownArea > 0 && (
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                          {greenhouseAreaBreakdown.map((gh) => {
                            const proportion = gh.area_ha / totalBreakdownArea;
                            const ghAmount = parseFloat((product.amount * proportion).toFixed(2));
                            return (
                              <span key={gh.greenhouse_name} className="text-primary text-xs font-medium">
                                {gh.greenhouse_name}: {ghAmount} {product.unit}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {!hasAreaBreakdown && numGreenhouses > 1 && (
                        <span className="text-primary text-xs font-medium">
                          ({parseFloat((product.amount / numGreenhouses).toFixed(2))} {product.unit}/{t("planner.perGreenhouse")})
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {product.rate_hint && (
                  <div className="mt-1.5 text-xs font-medium text-amber-700" data-testid="dose-habit-hint">
                    {product.rate_hint}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {numGreenhouses > 1 && products.length > 0 && (
        <div className="mt-3 p-2 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-center gap-2 text-xs text-primary">
            <Info className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
              {hasAreaBreakdown
                ? t("planner.quantitiesDistributedByArea", { count: numGreenhouses })
                : t("planner.quantitiesDividedAmongGreenhouses", { count: numGreenhouses })
              }
            </span>
          </div>
        </div>
      )}

      {isCustomizing && availableProducts.length > 0 && (
        <div className="mt-3 pt-3 border-t">
          <Select onValueChange={onAddProduct}>
            <SelectTrigger className="h-8 text-sm">
              <div className="flex items-center gap-1">
                <Plus className="h-3 w-3" />
                <span>{t("sprayLogs.addProductFromInventory")}</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              {availableProducts.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.product_name} ({formatStock(p.quantity)} {p.unit})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
    </div>
  );
}
